/**
 * F-019 — Restart-aware run controller.
 *
 * Coordinates the per-run state machine: PLAYING → GAME_OVER → restart()
 * → PLAYING. Owns the references each system needs to clear: simulation
 * registry, score, cooldown, planet queue, game-over detector.
 *
 * Keeps main.ts thin — it just hands the controller to UI callbacks.
 */

import type { Cooldown } from "./cooldown.ts";
import type { GameOverDetector } from "./game-over.ts";
import type { PlanetQueue } from "./planet-queue.ts";
import type { Score } from "./score.ts";
import type { SimulationRegistry } from "./spawn.ts";

export type RunPhase = "playing" | "game-over";

export interface RunController {
  phase: RunPhase;
  endRun(): void;
  restart(): void;
  isPlaying(): boolean;
  isGameOver(): boolean;
  onPhaseChange(listener: (phase: RunPhase) => void): () => void;
}

export interface RunControllerDeps {
  sim: SimulationRegistry;
  score: Score;
  cooldown: Cooldown;
  queue: PlanetQueue;
  detector: GameOverDetector;
  reseedQueue?: () => void;
}

export function createRunController(deps: RunControllerDeps): RunController {
  let phase: RunPhase = "playing";
  const listeners = new Set<(p: RunPhase) => void>();

  const announce = (next: RunPhase): void => {
    if (next === phase) return;
    phase = next;
    for (const l of listeners) l(phase);
  };

  const endRun = (): void => announce("game-over");

  const restart = (): void => {
    deps.sim.clear();
    deps.score.reset();
    deps.cooldown.reset();
    deps.detector.reset();
    deps.reseedQueue?.();
    announce("playing");
  };

  return {
    get phase() {
      return phase;
    },
    endRun,
    restart,
    isPlaying: () => phase === "playing",
    isGameOver: () => phase === "game-over",
    onPhaseChange: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}
